import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, ArrowRight, BookOpen, Send, MapPin, Tag, Check } from "lucide-react";
import { getPublicArticles, subscribeNewsletter } from "@/lib/api";
import { toast } from "sonner";

import { IMG_BLOG_HERO } from "../../data/images";
const BLOG_HERO = IMG_BLOG_HERO;

const CATEGORIES = [
  { key: "all", label: "Tous" },
  { key: "guide", label: "Guides locaux" },
  { key: "conseil", label: "Conseils propriétaires" },
  { key: "actualite", label: "Actualités" },
];

export default function BlogPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterStatus, setNewsletterStatus] = useState(null); // null | 'loading' | 'success'

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!newsletterEmail.trim() || !newsletterEmail.includes("@")) {
      toast.error("Veuillez entrer un email valide");
      return;
    }
    setNewsletterStatus("loading");
    try {
      await subscribeNewsletter(newsletterEmail.trim());
      setNewsletterStatus("success");
      setNewsletterEmail("");
      toast.success("Inscription réussie !");
    } catch (err) {
      if (err.response?.status === 409) {
        toast.info("Cet email est déjà inscrit");
      } else {
        toast.error("Erreur lors de l'inscription");
      }
      setNewsletterStatus(null);
    }
  };

  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    try {
      const res = await getPublicArticles();
      setArticles(res.data);
    } catch (error) {
      console.error("Error loading articles:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredArticles = activeCategory === "all"
    ? articles
    : articles.filter(a => a.categorie === activeCategory);

  const featuredArticle = articles.length > 0 ? articles[0] : null;
  const otherArticles = activeCategory === "all" ? articles.slice(1) : filteredArticles;

  return (
    <div data-testid="blog-page">
      {/* Hero with background */}
      <section className="relative py-24 overflow-hidden" data-testid="blog-hero">
        <div className="absolute inset-0">
          <img src={BLOG_HERO} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#2C1E14]/75" />
        </div>
        <div className="relative container-hh">
          <div className="max-w-3xl">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
              <span className="inline-block bg-[#C9922A]/20 text-[#C9922A] px-4 py-1.5 rounded-full text-sm font-semibold mb-4 backdrop-blur-sm border border-[#C9922A]/30">Blog</span>
              <h1 className="font-display text-4xl md:text-5xl text-white mb-6">L'Art de l'Investissement, le Plaisir de l'Accueil</h1>
              <p className="text-white/80 text-lg">
                Parce que l'immobilier de 2026 ne se gère plus avec les méthodes de 2010. Propriétaire visionnaire ou 
                voyageur exigeant, vous trouverez ici bien plus que des articles : une vision moderne du patrimoine. 
                Entre stratégies fiscales de pointe et guides d'art de vivre limousin, nous partageons avec vous les 
                clés d'une gestion immobilière d'exception.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Article */}
      {featuredArticle && activeCategory === "all" && (
        <section className="py-16 bg-[#F5F0EB]" data-testid="blog-featured">
          <div className="container-hh">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <Link to={`/blog/${featuredArticle.slug || featuredArticle.id}`} className="block group" data-testid="blog-featured-card">
                <div className="grid md:grid-cols-2 gap-8 bg-[#FFFDF9] rounded-2xl border border-[#E0D6CA] overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                  {featuredArticle.image ? (
                    <div className="aspect-[16/10] md:aspect-auto overflow-hidden">
                      <img src={featuredArticle.image} alt={featuredArticle.titre} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  ) : (
                    <div className="aspect-[16/10] md:aspect-auto bg-[#EDE8E1] flex items-center justify-center">
                      <BookOpen className="w-16 h-16 text-[#D4C9B8]" />
                    </div>
                  )}
                  <div className="p-8 flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="inline-block bg-[#C9922A]/10 text-[#C9922A] px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider">Article à la une</span>
                      <span className="flex items-center gap-1 text-xs text-[#8B7D6B]">
                        <Calendar className="w-3 h-3" />
                        {new Date(featuredArticle.date_publication || featuredArticle.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                      </span>
                    </div>
                    <h2 className="font-display text-2xl md:text-3xl text-[#004333] mb-4 group-hover:text-[#C9922A] transition-colors">{featuredArticle.titre}</h2>
                    <p className="text-[#8B7D6B] mb-6 leading-relaxed line-clamp-3">{featuredArticle.contenu?.substring(0, 250)}...</p>
                    <span className="text-[#C9922A] font-semibold text-sm flex items-center gap-2 group-hover:gap-3 transition-all">
                      Lire l'article complet <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* Category Filter */}
      <section className="bg-[#F5F0EB] pb-4">
        <div className="container-hh">
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeCategory === cat.key
                    ? "bg-[#004333] text-white shadow-md"
                    : "bg-white text-[#6B5E50] border border-[#E0D6CA] hover:border-[#C9922A]/40"
                }`}
                data-testid={`blog-cat-${cat.key}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="section-hh bg-[#F5F0EB]" data-testid="blog-grid">
        <div className="container-hh">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="card-hh animate-pulse">
                  <div className="aspect-[16/9] bg-[#E0D6CA]" />
                  <div className="p-6 space-y-3">
                    <div className="h-6 bg-[#E0D6CA] rounded w-3/4" />
                    <div className="h-4 bg-[#E0D6CA] rounded w-full" />
                    <div className="h-4 bg-[#E0D6CA] rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : otherArticles.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {otherArticles.map((article, index) => (
                <motion.div key={article.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}>
                  <Link to={`/blog/${article.slug || article.id}`} className="card-hh block group" data-testid={`blog-card-${article.id}`}>
                    {article.image ? (
                      <div className="aspect-[16/9] overflow-hidden">
                        <img src={article.image} alt={article.titre} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                    ) : (
                      <div className="aspect-[16/9] bg-[#EDE8E1] flex items-center justify-center">
                        <BookOpen className="w-12 h-12 text-[#D4C9B8]" />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center gap-2 text-sm text-[#8B7D6B] mb-3">
                        <Calendar className="w-4 h-4" />
                        {new Date(article.date_publication || article.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                      </div>
                      <h3 className="font-display text-xl text-[#004333] mb-3 group-hover:text-[#C9922A] transition-colors line-clamp-2">{article.titre}</h3>
                      <p className="text-[#8B7D6B] text-sm line-clamp-3 mb-4">{article.contenu?.substring(0, 150)}...</p>
                      <span className="text-[#C9922A] text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                        Lire la suite <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <BookOpen className="w-16 h-16 text-[#D4C9B8] mx-auto mb-4" />
              <h3 className="font-display text-xl text-[#004333] mb-2">
                {articles.length > 0 ? "Aucun article dans cette catégorie" : "Aucun article pour le moment"}
              </h3>
              <p className="text-[#8B7D6B]">
                {articles.length > 0 ? "Essayez une autre catégorie ou revenez bientôt." : "Revenez bientôt pour découvrir nos dernières actualités."}
              </p>
              {articles.length > 0 && (
                <button onClick={() => setActiveCategory("all")} className="mt-4 text-[#C9922A] font-medium hover:underline text-sm">
                  Voir tous les articles
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 bg-[#1A120B]" data-testid="blog-newsletter">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-14 h-14 rounded-full bg-[#C9922A]/10 flex items-center justify-center mx-auto mb-6">
            {newsletterStatus === "success" ? <Check className="w-6 h-6 text-green-400" /> : <Send className="w-6 h-6 text-[#C9922A]" />}
          </div>
          {newsletterStatus === "success" ? (
            <>
              <h2 className="font-display text-3xl md:text-4xl text-white mb-4">Merci pour votre inscription !</h2>
              <p className="text-white/50 max-w-lg mx-auto">Vous recevrez nos prochains articles directement dans votre boîte mail.</p>
            </>
          ) : (
            <>
              <h2 className="font-display text-3xl md:text-4xl text-white mb-4">Restez informé</h2>
              <p className="text-white/50 mb-8 max-w-lg mx-auto">
                Recevez nos derniers articles, conseils immobiliers et actualités directement dans votre boîte mail.
              </p>
              <form onSubmit={handleNewsletter} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="Votre adresse email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  required
                  className="flex-1 px-5 py-3.5 rounded-full bg-white/[0.08] border border-white/15 text-white placeholder-white/30 focus:border-[#C9922A]/50 focus:ring-2 focus:ring-[#C9922A]/10 outline-none transition-all"
                  data-testid="newsletter-email"
                />
                <button
                  type="submit"
                  disabled={newsletterStatus === "loading"}
                  className="px-6 py-3.5 bg-[#C9922A] text-white rounded-full font-semibold hover:bg-[#B8831F] transition-colors whitespace-nowrap disabled:opacity-50"
                  data-testid="newsletter-submit"
                >
                  {newsletterStatus === "loading" ? "..." : "S'abonner"}
                </button>
              </form>
              <p className="text-white/20 text-xs mt-4">Pas de spam. Désinscription en un clic.</p>
            </>
          )}
        </div>
      </section>

      {/* Guides locaux teaser */}
      <section className="py-20 bg-[#F5F0EB]" data-testid="blog-guides">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#C9922A] uppercase tracking-[0.2em] text-sm font-semibold mb-3 block">Guides locaux</span>
            <h2 className="font-display text-3xl md:text-4xl text-[#004333]">Explorez nos destinations</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Limoges : 10 incontournables",
                desc: "Découvrez les trésors cachés de la Ville de Limoges : musées, gastronomie, patrimoine et activités pour un séjour inoubliable.",
                image: "/images/photo-1659027793165.jpg",
                slug: "limoges-10-incontournables-pour-un-sejour-inoubliable",
                badge: "Limoges",
              },
              {
                title: "Évaux-les-Bains : guide complet",
                desc: "Tout savoir pour votre cure thermale à Évaux-les-Bains : hébergement, soins, activités et bonnes adresses.",
                image: "/images/photo-1760372060494.jpg",
                slug: "evaux-les-bains-guide-complet-pour-votre-cure-thermale",
                badge: "Évaux-les-Bains",
              },
            ].map((guide, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <Link to={`/blog/${guide.slug}`} className="block group overflow-hidden rounded-2xl border border-[#E0D6CA] bg-[#FFFDF9] shadow-sm hover:shadow-md transition-shadow" data-testid={`guide-card-${i}`}>
                  <div className="aspect-[16/9] overflow-hidden relative">
                    <img src={guide.image} alt={guide.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute top-4 left-4">
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-semibold text-[#004333]">
                        <MapPin className="w-3 h-3 text-[#C9922A]" /> {guide.badge}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-xl text-[#004333] mb-2 group-hover:text-[#C9922A] transition-colors">{guide.title}</h3>
                    <p className="text-[#8B7D6B] text-sm leading-relaxed mb-4">{guide.desc}</p>
                    <span className="text-[#C9922A] text-sm font-semibold flex items-center gap-2 group-hover:gap-3 transition-all">
                      Lire le guide <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
